import { useMemo, useState } from "react";
import {
  KeyRound,
  Plus,
  Search,
  Lock,
  Trash2,
  Boxes,
  ShieldCheck,
  Briefcase,
  RefreshCw,
} from "lucide-react";
import styles from "./PermissionsCatalog.module.css";
import AddPermissionModal from "../../../components/UsersPermissions/AddPermissionModal";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  usePermissionsList,
  useDeletePermission,
} from "../../../api/users/usePermissionsApi";
import {
  groupPermissionsByModule,
  getActionMeta,
  humanize,
} from "../../../constants/permissionMeta";

const PermissionsCatalog = () => {
  const { showToast } = useToast();
  const { data: permissions, meta, loading, error, refetch } = usePermissionsList();
  const { execute: deletePermission, loading: deleting } = useDeletePermission();

  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [permToDelete, setPermToDelete] = useState(null);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? permissions.filter(
          (p) =>
            p.key?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.module?.toLowerCase().includes(q)
        )
      : permissions;
    return groupPermissionsByModule(filtered);
  }, [permissions, query]);

  const tiles = [
    {
      label: "Total Permissions",
      value: meta?.total ?? permissions.length ?? "—",
      icon: <KeyRound size={20} />,
      tone: "blue",
    },
    {
      label: "Modules",
      value: meta?.modules ? Object.keys(meta.modules).length : "—",
      icon: <Boxes size={20} />,
      tone: "orange",
    },
    {
      label: "System",
      value: meta?.systemPermissions ?? "—",
      icon: <ShieldCheck size={20} />,
      tone: "purple",
    },
    {
      label: "Business",
      value: meta?.businessPermissions ?? "—",
      icon: <Briefcase size={20} />,
      tone: "green",
    },
  ];

  const handleConfirmDelete = async () => {
    if (!permToDelete) return;
    try {
      await deletePermission(permToDelete.id);
      showToast("Permission deleted successfully", "success");
      setPermToDelete(null);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete permission", "error");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <KeyRound size={22} className={styles.titleIcon} />
            Permissions Catalog
          </h1>
          <p className={styles.subtitle}>
            Every action-level permission, grouped by module
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshBtn} onClick={() => refetch()}>
            <RefreshCw size={16} />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className={styles.addBtn} onClick={() => setShowAdd(true)}>
            <Plus size={16} />
            Add Permission
          </button>
        </div>
      </div>

      <div className={styles.statGrid}>
        {tiles.map((t) => (
          <div key={t.label} className={`${styles.statCard} ${styles[t.tone]}`}>
            <div className={styles.statIcon}>{t.icon}</div>
            <div>
              <div className={styles.statValue}>{t.value}</div>
              <div className={styles.statLabel}>{t.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.searchBar}>
        <Search size={16} className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          placeholder="Search permissions by key, module or description…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading permissions…</div>
      ) : groups.length === 0 ? (
        <div className={styles.loading}>No permissions match your search.</div>
      ) : (
        <div className={styles.groups}>
          {groups.map((group) => (
            <div key={group.key} className={styles.group}>
              <div
                className={styles.groupHeader}
                style={{ borderLeftColor: group.accent }}
              >
                <span className={styles.groupIcon}>{group.icon}</span>
                <span className={styles.groupLabel}>{group.label}</span>
                <span className={styles.groupCount}>
                  {group.permissions.length}
                </span>
              </div>
              <div className={styles.permList}>
                {group.permissions.map((p) => {
                  const am = getActionMeta(p.action);
                  return (
                    <div key={p.id} className={styles.permRow}>
                      <span
                        className={styles.actionChip}
                        style={{ background: am.bg, color: am.color }}
                      >
                        {am.label}
                      </span>
                      <div className={styles.permInfo}>
                        <span className={styles.permResource}>
                          {humanize(p.resource)}
                        </span>
                        <span className={styles.permKey}>{p.key}</span>
                      </div>
                      <span className={styles.permDesc}>
                        {p.description || "—"}
                      </span>
                      {p.isSystem ? (
                        <span className={styles.sysTag} title="System permission">
                          <Lock size={12} /> System
                        </span>
                      ) : (
                        <button
                          className={styles.delBtn}
                          title="Delete permission"
                          onClick={() => setPermToDelete(p)}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddPermissionModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => {
          setShowAdd(false);
          refetch();
        }}
      />

      <DeleteConfirmModal
        open={!!permToDelete}
        onClose={() => setPermToDelete(null)}
        title="Delete permission"
        itemName={permToDelete?.key}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default PermissionsCatalog;
